import { useState, useEffect } from 'react'
import {
  Container,
  SearchField,
  useDebounce,
  XStack,
  Stack,
  SmartLink,
  YStack,
  SizableText,
  ScrollView,
  isWeb,
  Button,
  Spinner,
} from '@my/ui'
import { MainLayout, Meta, NativePageNavigation, H2, H3 } from 'app/components'
import { SearchNoResults, SearchFilters } from '../../components'
import {
  SearchArticlesQueryOutput,
  SearchBrandsQueryOutput,
  SearchOffersQueryOutput,
} from '../../queries'
import { BrandTabItem } from 'app/features/brands/components'
import { OfferCard } from 'app/features/offer/components'
import { SearchFilter } from './search-screen.container'
import { ArticleCard } from 'app/features/blog/components'

const searchFilters: {
  label: string
  name: SearchFilter
}[] = [
  {
    label: 'All',
    name: '',
  },
  {
    label: 'Discounts',
    name: 'offers',
  },
  {
    label: 'Brands',
    name: 'brands',
  },
  {
    label: 'Life',
    name: 'articles',
  },
]

export interface SearchScreenProps {
  initialQuery: string
  filter: SearchFilter

  articles: SearchArticlesQueryOutput['searchArticles']['docs']
  articlesHasMore: boolean
  articlesFetchMore: () => void
  articlesFetchingMore: boolean

  brands: SearchBrandsQueryOutput['searchBrands']['docs']
  brandsHasMore: boolean
  brandsFetchMore: () => void
  brandsFetchingMore: boolean

  offers: SearchOffersQueryOutput['searchOffers']['docs']
  offersHasMore: boolean
  offersFetchMore: () => void
  offersFetchingMore: boolean

  loading: boolean
  fetching: boolean

  onFilterChange: (filter: string) => void
  onSearch: (query: string) => void
}

export const SearchScreen = (props: SearchScreenProps) => {
  const [value, setValue] = useState(props.initialQuery)
  const debouncedOnSearch = useDebounce(props.onSearch, 250)

  const handleChange = (newText) => {
    setValue(newText)
    debouncedOnSearch(newText)
  }

  useEffect(() => {
    if (props.initialQuery) {
      props.onSearch(props.initialQuery)
    }
  }, [props.initialQuery, props.onSearch])

  const totalResults = props.articles.length + props.brands.length + props.offers.length

  // Show more items if filter is selected
  const brands = !!props.filter ? props.brands : props.brands.slice(0, 4)
  const offers = !!props.filter ? props.offers : props.offers.slice(0, 6)
  const articles = !!props.filter ? props.articles : props.articles.slice(0, 3)

  return (
    <MainLayout>
      <Meta title="Search" />
      {!isWeb && <NativePageNavigation title="Search" />}
      <ScrollView>
        <Container gap="$6" spaceTop spaceBottom gutter size="base">
          {value && value.length > 0 && (
            <XStack
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              marginBottom="$2"
              pb="$3"
              bbw={1}
              boc="$overlay6"
            >
              <XStack gap="$2">
                <H2 color="$gray10">Results for</H2>
                <H2 color="$black">{value}</H2>
              </XStack>

              {!props.loading && !props.fetching && (
                <Stack p="$2" bg="$gray6" br="$3" h="$2.5">
                  <SizableText color="$grey6" size="$2" fow="600">
                    {totalResults}
                  </SizableText>
                </Stack>
              )}
              {(props.loading || props.fetching) && (
                <Stack h="$2.5" ai="center" jc="center">
                  <Spinner size="small" color="$black" />
                </Stack>
              )}
            </XStack>
          )}

          <SearchField
            display="flex"
            h={60}
            w="100%"
            value={value}
            inputProps={{
              placeholder: 'Items, Brands, Life and more!',
            }}
            onSearchChange={handleChange}
          />

          <SearchFilters
            items={searchFilters}
            onFilterChange={props.onFilterChange}
            selectedFilter={props.filter}
          />

          {props.loading && (
            <XStack minHeight={100} jc="center" ai="center">
              <Spinner size="large" color="$black" />
            </XStack>
          )}

          {(brands?.length || 0) > 0 && (!props.filter || props.filter === 'brands') && (
            <YStack gap="$4">
              <XStack jc="space-between" columnGap="$3.5" rowGap="$2" ai="center" flexWrap="wrap">
                <H3>Brands</H3>
                <SmartLink
                  Component={Button}
                  size="$3"
                  fontWeight="500"
                  theme="gray"
                  href="/brands"
                >
                  View All
                </SmartLink>
              </XStack>
              <XStack flexWrap="wrap" m={-8}>
                {brands.map((brand) => (
                  <Stack
                    key={brand.id}
                    p={8}
                    w="100%"
                    $gtXs={{
                      w: '33.33%',
                    }}
                    $gtSm={{
                      w: '25%',
                    }}
                  >
                    <BrandTabItem
                      id={brand.id}
                      logo={{
                        url: brand.logo?.url || '',
                        alt: '',
                      }}
                      name={brand.name}
                      slug={brand.slug}
                      description={brand.description}
                    />
                  </Stack>
                ))}
              </XStack>
              {props.brandsHasMore && !!props.filter && (
                <XStack jc="center">
                  <Button
                    theme="primary"
                    onPress={props.brandsFetchMore}
                    loading={props.brandsFetchingMore}
                  >
                    Load more
                  </Button>
                </XStack>
              )}
            </YStack>
          )}

          {!props.loading && totalResults === 0 && <SearchNoResults />}

          {(offers?.length || 0) > 0 && (!props.filter || props.filter === 'offers') && (
            <YStack gap="$4">
              <XStack jc="space-between" columnGap="$3.5" rowGap="$2" ai="center" flexWrap="wrap">
                <H3>Explore discounts</H3>
                <SmartLink
                  fontWeight="500"
                  Component={Button}
                  size="$3"
                  theme="gray"
                  href="/student-discount"
                >
                  View All
                </SmartLink>
              </XStack>
              <XStack flexWrap="wrap" m={-8}>
                {offers.map((offer) => (
                  <Stack
                    key={offer.id}
                    w="100%"
                    p={8}
                    $gtXs={{
                      w: '50%',
                    }}
                    $gtMd={{
                      w: '33.33%',
                    }}
                  >
                    <OfferCard {...offer} />
                  </Stack>
                ))}
              </XStack>
              {props.offersHasMore && !!props.filter && (
                <XStack jc="center">
                  <Button
                    theme="primary"
                    onPress={props.offersFetchMore}
                    loading={props.offersFetchingMore}
                  >
                    Load more
                  </Button>
                </XStack>
              )}
            </YStack>
          )}

          {(articles?.length || 0) > 0 && (!props.filter || props.filter === 'articles') && (
            <YStack gap="$4">
              <XStack jc="space-between" columnGap="$3.5" rowGap="$2" ai="center" flexWrap="wrap">
                <H3>Life</H3>
                <SmartLink href="/life" Component={Button} size="$3" fontWeight="500" theme="gray">
                  View All
                </SmartLink>
              </XStack>
              <XStack flexWrap="wrap" m={-8}>
                {articles.map((article) => (
                  <Stack
                    key={article.id}
                    p={8}
                    w="100%"
                    $gtXs={{
                      w: '50%',
                    }}
                    $gtMd={{
                      w: '33.33%',
                    }}
                  >
                    <ArticleCard
                      {...article}
                      wrapperStyle={{
                        flex: 1,
                      }}
                    />
                  </Stack>
                ))}
              </XStack>
              {props.articlesHasMore && !!props.filter && (
                <XStack jc="center">
                  <Button
                    theme="primary"
                    onPress={props.articlesFetchMore}
                    loading={props.articlesFetchingMore}
                  >
                    Load more
                  </Button>
                </XStack>
              )}
            </YStack>
          )}
        </Container>
      </ScrollView>
    </MainLayout>
  )
}
